import { Controller } from "@hotwired/stimulus"
import { post, destroy } from "@rails/request.js"
import { escapeHtml } from "../lib/html_sanitizer"
import { syncGateReason } from "../lib/gate_reason"
import { showConfirmModal } from "../lib/custom_confirm"
import { markButtonBusy, clearButtonBusy } from "../lib/button_loading"

// The prompt's words live in Ruby (Cart::COUPON_RESTRICTIONS_PROMPT, #16582):
// the order's coupon note quotes exactly what the agent agreed to, and
// carts/show stamps the same text as the `restrictionsPrompt` value. This
// default only serves a DOM that stamps nothing (the Jest fixtures). It must
// equal Cart::COUPON_RESTRICTIONS_PROMPT; no spec pins the two together now.
const RESTRICTIONS_PROMPT = "Coupon Applied. This coupon will disable all Lead Order Restrictions. Do you want to still use this coupon?"
const RESTRICTIONS_NOTICE = "This coupon disables all Lead Order Restrictions on your Real-Time orders."
const ACKNOWLEDGE_FAILED = "We couldn't record your confirmation. Reload the page and confirm the coupon again before checking out."

// The attributes lib/gate_reason writes on a gated button, so a temporary hold
// can hand back exactly what the button's own gate left there.
const REASON_ATTRIBUTES = ["data-gl-tooltip", "aria-label", "aria-disabled", "title"]
const reasonSnapshot = (el) => REASON_ATTRIBUTES.map((name) => [name, el.getAttribute(name)])
const restoreReason = (el, snapshot) => snapshot.forEach(([name, value]) => {
  if (value === null) el.removeAttribute(name)
  else el.setAttribute(name, value)
})

// The cart is the coupon's memory: Apply (POST /cart/validate_coupon) persists
// the coupon on the cart, Remove (DELETE /cart/remove_coupon) forgets it, and a
// re-rendered page hands the remembered coupon back through the `applied` value
// so the panel reopens exactly as the buyer left it. The hidden form field is
// only the page's hint — checkout falls back to the cart when it posts blank —
// so a reload, a second tab or back navigation can no longer charge full price.
//
// A11Y-1 / BUTTON-4 (#13355): every disabled frame of Apply — and of Checkout
// while Remove holds it — says which gate holds it, as the button's own
// gl-tooltip plus a "<label> — <reason>" accessible name (lib/gate_reason),
// withdrawn the moment the gate opens. The gates are unchanged.
export default class extends Controller {
  static targets = ["input", "applyBtn", "message", "summary", "discountText", "cartTotal"]
  // The reasons are authored ONCE, in carts/show, which paints the first-frame
  // reason server-side and stamps each one here as a value. The defaults only
  // serve a DOM that stamps nothing (the Jest fixtures).
  static values = {
    applied: Object,
    removeUrl: String,
    acknowledgeUrl: { type: String, default: "/cart/acknowledge_coupon_restrictions" },
    restrictionsPrompt: { type: String, default: RESTRICTIONS_PROMPT },
    agreeReason: { type: String, default: "Check the agreement box first" },
    entryReason: { type: String, default: "Enter a coupon code of at least 3 characters" },
    appliedReason: { type: String, default: "Remove this coupon to use a different one" },
    validatingReason: { type: String, default: "Checking your coupon code…" },
    removingReason: { type: String, default: "Removing your coupon…" }
  }

  connect() {
    const remembered = this.hasAppliedValue ? this.appliedValue : null
    if (remembered?.coupon?.code) {
      this.inputTarget.value = remembered.coupon.code
      this.showSuccess(remembered)
      this.setSubmittedCode(remembered.coupon.code)
    }
    this.validateInput()
    // #16388: a remembered Disable Restrictions coupon the buyer never confirmed
    // — applied before a Real-Time order joined the cart, or flagged by an admin
    // since — is asked about now; checkout refuses it until they answer.
    if (remembered?.disables_restrictions && !remembered.restrictions_acknowledged) {
      this.confirmRestrictionsDisabled(remembered.coupon.code)
    }
  }

  // True while a coupon is applied. Kept on the input's dataset (not only in
  // controller state) so cart_controller's agreement gating can read it without
  // reaching into this controller.
  get applied() {
    return this.inputTarget.dataset.couponApplied === "true"
  }

  validateInput() {
    if (this.applied) {
      syncGateReason(this.applyBtnTarget, { ready: false, reason: this.appliedReasonValue })
      return
    }
    const code = this.inputTarget.value.trim()
    const isValid = code.length >= 3
    syncGateReason(this.applyBtnTarget, { ready: isValid, reason: this.applyHoldReason() })
  }

  // Why an unapplied Apply is held: a field cart_controller's agreement gate
  // keeps locked cannot take a code (only that gate or an applied coupon locks
  // it); an open field holds a code too short for the gate above.
  applyHoldReason() {
    return this.inputTarget.disabled ? this.agreeReasonValue : this.entryReasonValue
  }

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const code = this.inputTarget.value.trim()
      if (code.length >= 3 && !this.applied) {
        this.applyCoupon()
      }
    }
  }

  async applyCoupon() {
    const code = this.inputTarget.value.trim().toUpperCase()

    if (!code) {
      this.showError("Please enter a coupon code")
      return
    }

    // BUTTON-8: Apply keeps its label while the check runs — the shared
    // spinner, aria-busy, and the gate's validating reason, which also blocks a
    // second click. Busy clears the moment the answer lands, before any re-gate
    // (lib/gate_reason never re-opens a busy button).
    markButtonBusy(this.applyBtnTarget)
    syncGateReason(this.applyBtnTarget, { ready: false, reason: this.validatingReasonValue })
    this.clearMessages()

    try {
      const response = await post("/cart/validate_coupon", {
        body: JSON.stringify({ coupon_code: code }),
        contentType: "application/json",
        responseKind: 'json',
      })

      const data = await response.json
      clearButtonBusy(this.applyBtnTarget)

      if (response.ok && data.valid) {
        this.showSuccess(data)
        // The page's hint for checkout; the server already remembers the coupon.
        this.setSubmittedCode(code)
        if (data.disables_restrictions) await this.confirmRestrictionsDisabled(data.coupon.code)
      } else {
        this.showError(data.message || "Invalid coupon code")
        syncGateReason(this.applyBtnTarget, { ready: true })
      }
    } catch (error) {
      clearButtonBusy(this.applyBtnTarget)
      console.error("Error validating coupon:", error)
      this.showError("Error validating coupon code. Please try again.")
      syncGateReason(this.applyBtnTarget, { ready: true })
    }
  }

  // #16388: a "Disable Restrictions" coupon removes every Lead Order
  // Restriction on the Real-Time orders it discounts. Asked on a fresh Apply
  // and on a remembered coupon the cart has no answer for (connect). "Use
  // Coupon" records the answer on the cart — checkout refuses the coupon
  // without it — and "Remove Coupon" runs the same server-side Remove as the
  // panel's Remove control, with Remove and Checkout locked for the round trip
  // so a checkout submitted meanwhile cannot post the still-set code. If the
  // Remove fails, forgetOnServer reports it and the panel keeps saying "applied".
  async confirmRestrictionsDisabled(code) {
    const keep = await showConfirmModal(this.restrictionsPromptValue, {
      title: "Coupon Applied",
      confirmText: "Use Coupon",
      cancelText: "Remove Coupon",
      variant: "primary",
    })
    if (keep) {
      await this.acknowledgeOnServer(code)
      return
    }
    if (!this.hasRemoveUrlValue || this.removing) return

    this.removing = true
    const unlock = this.lockControls(this.element.querySelector("[data-action='click->coupon-code#removeCoupon']"))
    let forgotten = false
    try {
      forgotten = await this.forgetOnServer()
    } finally {
      unlock()
      this.removing = false
    }
    if (forgotten) this.resetToUnapplied()
  }

  // Record "Use Coupon" on the cart. A failure leaves the coupon applied and
  // says so; checkout would refuse it until the buyer confirms again.
  async acknowledgeOnServer(code) {
    try {
      const response = await post(this.acknowledgeUrlValue, {
        body: JSON.stringify({ coupon_code: code }),
        contentType: "application/json",
        responseKind: "json",
      })
      if (!response.ok) throw new Error(`HTTP ${response.statusCode}`)
      return true
    } catch (error) {
      console.error("Error acknowledging coupon restrictions:", error)
      this.showAcknowledgeError()
      return false
    }
  }

  formatCurrency(amount) {
    const numericValue = typeof amount === 'string'
      ? parseFloat(amount.replace(/[$,]/g, ''))
      : amount

    // Guard NaN/undefined/null (a missing or malformed server amount) so the
    // Total line never renders "$NaN" — fall back to $0.00.
    if (!Number.isFinite(numericValue)) return '$0.00'

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericValue)
  }

  showSuccess(data) {
    if (window.showToast) {
      const formattedDiscount = this.formatCurrency(data.amounts.discount)
      const toastMessage = `Coupon applied! You saved ${formattedDiscount}`
      window.showToast(toastMessage, "bg-success", 5000)
    }

    this.messageTarget.innerHTML = `
      <div class="gl-alert gl-alert--success" role="alert">
        <i class="ph-fill ph-check-circle"></i>
        <div class="gl-alert-content">
          <div class="gl-alert-title">Coupon Applied!</div>
          <p class="mb-0">${escapeHtml(data.message)}</p>
        </div>
      </div>
    `

    let discountText = ""
    if (data.coupon.discount_type === "percentage") {
      discountText = `${data.coupon.discount_value}% discount applied`
    } else if (data.coupon.discount_type === "tiered_percentage") {
      discountText = `${data.coupon.discount_display} discount applied`
    } else {
      // voucher / fixed_amount: the pooled allocation caps at the cart total, so
      // show the discount the server APPLIED (amounts.discount, already formatted),
      // never the coupon's face value — a $250 voucher on a $180 cart saves $180.
      discountText = `${data.amounts.discount} discount applied`
    }

    if (data.applicable_items_count < data.total_items_count) {
      discountText += ` to ${data.applicable_items_count} of ${data.total_items_count} items`
    }

    this.discountTextTarget.textContent = discountText

    // Show discount summary
    this.summaryTarget.classList.remove("d-none")
    this.summaryTarget.innerHTML = `
      <div class="gl-alert gl-alert--success mb-0" role="alert">
        <i class="ph-fill ph-check-circle"></i>
        <div class="gl-alert-content">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="gl-alert-title">Discount Applied: ${escapeHtml(data.coupon.code)}</div>
              <p class="mb-1">${escapeHtml(discountText)}</p>
              ${data.covers_full_amount ? '<p class="mb-0"><strong class="text-success">✓ This order is FREE!</strong></p>' : ''}
              ${data.disables_restrictions ? `<p class="mb-0" data-coupon-restrictions-notice>${escapeHtml(RESTRICTIONS_NOTICE)}</p>` : ''}
            </div>
            <button type="button" class="gl-alert-dismiss" data-action="click->coupon-code#removeCoupon" aria-label="Remove coupon" data-gl-tooltip="Remove coupon">
              <i class="ph ph-x"></i>
            </button>
          </div>
          <hr class="my-2">
          <div class="row small">
            <div class="col-6">
              <div>Original Total:</div>
              <div>Discount:</div>
              ${!data.covers_full_amount ? '<div><strong>Final Total:</strong></div>' : ''}
            </div>
            <div class="col-6 text-end">
              <div>${escapeHtml(data.amounts.original_total)}</div>
              <div>-${escapeHtml(data.amounts.discount)}</div>
              ${!data.covers_full_amount ? `<div><strong>${escapeHtml(data.amounts.final_total)}</strong></div>` : ''}
            </div>
          </div>
        </div>
      </div>
    `

    // Paint the discount on each cart line (#14073): the struck list price,
    // the discounted price and "-$X" per order, or why the coupon skipped it.
    this.paintLines(data.items)

    // Reflect the discount on the cart's Total line (the summary banner alone
    // left the old total showing — the buyer must see the price they'll pay).
    if (this.hasCartTotalTarget) {
      if (this.originalCartTotal === undefined) this.originalCartTotal = this.cartTotalTarget.textContent
      // The backend sends "$1132.75" (no thousands separators); the page's
      // original total came from number_to_currency ("$1,132.75") — reformat
      // so the Total line stays consistent with the rest of the page.
      this.cartTotalTarget.textContent = this.formatCurrency(data.amounts.final_total)
    }

    // Lock the field while applied; only Remove reopens it.
    this.inputTarget.dataset.couponApplied = "true"
    this.inputTarget.disabled = true
    this.applyBtnTarget.textContent = "Applied"
    syncGateReason(this.applyBtnTarget, { ready: false, reason: this.appliedReasonValue })
    this.applyBtnTarget.classList.remove("gl-btn-primary")
    this.applyBtnTarget.classList.add("gl-btn-success")
  }

  showError(message) {
    this.messageTarget.innerHTML = `
      <div class="gl-alert gl-alert--danger" role="alert">
        <i class="ph-fill ph-x-circle"></i>
        <div class="gl-alert-content">${escapeHtml(message)}</div>
      </div>
    `
    this.summaryTarget.classList.add("d-none")

    // A rejected Apply also cleared the server's memory; mirror it here.
    delete this.inputTarget.dataset.couponApplied
    this.setSubmittedCode("")

    this.restoreCartTotal()
    this.resetLines()
  }

  // Restore the undiscounted cart Total
  restoreCartTotal() {
    if (this.hasCartTotalTarget && this.originalCartTotal !== undefined) {
      this.cartTotalTarget.textContent = this.originalCartTotal
      this.originalCartTotal = undefined
    }
  }

  // Every cart line's price stack (carts/_cart_line_price): the desktop row AND
  // the mobile card both render one per order, keyed by lead_order_id, so a
  // line is painted wherever it appears. Lines live outside this controller's
  // element (the items table sits above the summary panel), like the hidden
  // checkout field.
  lineElements(leadOrderId) {
    const selector = leadOrderId == null
      ? "[data-coupon-line-id]"
      : `[data-coupon-line-id="${leadOrderId}"]`
    return Array.from(document.querySelectorAll(selector))
  }

  // Applies the quote's per-order verdicts to the lines. A payload without
  // `items` (older server) leaves the lines at their resting state.
  paintLines(items) {
    this.resetLines()
    if (!Array.isArray(items)) return

    items.forEach((item) => {
      this.lineElements(item.lead_order_id).forEach((line) => {
        const list = line.querySelector("[data-coupon-line='list']")
        const final = line.querySelector("[data-coupon-line='final']")
        const discount = line.querySelector("[data-coupon-line='discount']")
        const reason = line.querySelector("[data-coupon-line='reason']")

        if (item.applicable && Number(item.discount_cents) > 0) {
          list?.classList.add("gl-cart-line-list--struck")
          if (final) {
            final.textContent = this.formatCurrency(item.final)
            final.classList.remove("d-none")
          }
          if (discount) {
            discount.textContent = `-${this.formatCurrency(item.discount)} coupon`
            discount.classList.remove("d-none")
          }
        } else if (!item.applicable && reason && item.reason) {
          reason.textContent = item.reason
          reason.classList.remove("d-none")
        }
      })
    })
  }

  // Back to the server-rendered resting state: list price only.
  resetLines() {
    this.lineElements().forEach((line) => {
      line.querySelector("[data-coupon-line='list']")?.classList.remove("gl-cart-line-list--struck")
      ;["final", "discount", "reason"].forEach((part) => {
        const el = line.querySelector(`[data-coupon-line='${part}']`)
        if (!el) return
        el.textContent = ""
        el.classList.add("d-none")
      })
    })
  }

  clearMessages() {
    this.messageTarget.innerHTML = ""
    this.summaryTarget.classList.add("d-none")
  }

  // The checkout form's hidden coupon field (inside #checkout-form). Absent in
  // fixtures that mount only the panel.
  setSubmittedCode(code) {
    const field = document.getElementById("hidden_coupon_code_submit")
    if (field) field.value = code
  }

  // Confirm-then-clear: the server is the memory, so the panel only forgets the
  // coupon once the server has. Clearing first would let a checkout submitted
  // while the DELETE is in flight post a blank field and fall back to the coupon
  // the buyer just watched disappear, and a failed DELETE would leave the page
  // saying "no coupon" while the server still remembers one. Remove and Checkout
  // are locked for the round trip; on failure the applied state stays as it was.
  async removeCoupon(event) {
    event.preventDefault()
    if (this.removing) return

    if (this.applied && this.hasRemoveUrlValue) {
      this.removing = true
      const unlock = this.lockControls(event.currentTarget)
      let forgotten = false
      try {
        forgotten = await this.forgetOnServer()
      } finally {
        unlock()
        this.removing = false
      }
      if (!forgotten) return
    }

    this.resetToUnapplied()
  }

  resetToUnapplied() {
    delete this.inputTarget.dataset.couponApplied
    this.inputTarget.value = ""
    this.inputTarget.disabled = false
    this.applyBtnTarget.textContent = "Apply"
    // The field re-opens unconditionally (the agreement locks it again on its
    // next change, #13355), so today this names the empty code.
    // It reads the live field so the reason stays true if Remove ever re-locks it.
    syncGateReason(this.applyBtnTarget, { ready: false, reason: this.applyHoldReason() })
    this.applyBtnTarget.classList.remove("gl-btn-success")
    this.applyBtnTarget.classList.add("gl-btn-primary")
    this.clearMessages()
    this.setSubmittedCode("")
    this.restoreCartTotal()
    this.resetLines()
  }

  // Disable the Remove control and the Checkout button for the DELETE round
  // trip; the returned function restores each to the state it was in (Checkout
  // belongs to cart_controller, which may legitimately hold it disabled). While
  // held, Checkout names the removal as its reason, and the restore hands back
  // the exact reason attributes cart_controller had left on it.
  lockControls(removeButton) {
    const checkout = document.getElementById("btn-checkout")
    const controls = [removeButton, checkout]
      .filter((el) => el instanceof HTMLElement)
      .map((el) => ({ el, wasDisabled: el.disabled, reason: el === checkout ? reasonSnapshot(el) : null }))
    controls.forEach(({ el }) => {
      if (el === checkout) syncGateReason(el, { ready: false, reason: this.removingReasonValue })
      else el.disabled = true
    })
    return () => controls.forEach(({ el, wasDisabled, reason }) => {
      el.disabled = wasDisabled
      if (reason) restoreReason(el, reason)
    })
  }

  // true once the server has forgotten the coupon; false (with the applied
  // state untouched and an error shown) when it has not.
  async forgetOnServer() {
    try {
      const response = await destroy(this.removeUrlValue, { responseKind: "json" })
      if (!response.ok) throw new Error(`HTTP ${response.statusCode}`)
      return true
    } catch (error) {
      console.error("Error removing coupon:", error)
      this.showRemovalError()
      return false
    }
  }

  showAcknowledgeError() {
    this.messageTarget.innerHTML = `
      <div class="gl-alert gl-alert--danger" role="alert">
        <i class="ph-fill ph-x-circle"></i>
        <div class="gl-alert-content">${escapeHtml(ACKNOWLEDGE_FAILED)}</div>
      </div>
    `
  }

  // Report a failed Remove WITHOUT touching the applied state — the coupon is
  // still applied on the server, so the panel must keep saying so.
  showRemovalError() {
    this.messageTarget.innerHTML = `
      <div class="gl-alert gl-alert--danger" role="alert">
        <i class="ph-fill ph-x-circle"></i>
        <div class="gl-alert-content">We couldn't remove the coupon. It is still applied — try again or reload the page.</div>
      </div>
    `
  }
}
